import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Search, Bell, Keyboard } from "lucide-react";
import { Modal } from "../ui/Modal";

const FAQ_ITEMS: { q: string; a: string }[] = [
  {
    q: "Hoe maak ik een factuur?",
    a: "Ga naar Facturen en klik op 'Nieuwe factuur'. Kies een contact (of maak er eerst één aan via Contacten), vul de regels in en sla op als concept. Verstuur daarna per e-mail of download de PDF.",
  },
  {
    q: "Hoe werkt Offerte → Factuur?",
    a: "Bij een geaccepteerde offerte kun je via het actiemenu 'Naar factuur' kiezen. Alle regels en klantgegevens worden overgenomen; de offerte wordt gemarkeerd als gefactureerd.",
  },
  {
    q: "Hoe scan ik een bon of uitgave?",
    a: "Ga naar Uitgaven en klik op 'Scannen'. Upload een foto of PDF; OCR leest automatisch bedrag, BTW en leverancier in. Controleer de gegevens altijd voordat je opslaat.",
  },
  {
    q: "Wat betekenen de meldingen bij de bel?",
    a: "De bel toont actuele aandachtspunten: achterstallige facturen, offertes die (bijna) verlopen, mislukte scans, concepten die klaarliggen en betalingen zonder factuur. Klik op een melding om naar de bijbehorende pagina te gaan.",
  },
  {
    q: "Waar stel ik mijn logo, e-mail en factuurtemplate in?",
    a: "Ga naar Instellingen. Daar regel je bedrijfsgegevens, logo, e-mailconfiguratie (SMTP), BTW-tarieven, teamleden en de lay-out van je facturen en offertes.",
  },
  {
    q: "Hoe werken meerdere valuta's?",
    a: "Je kunt documenten in een andere valuta opmaken; de wisselkoers wordt automatisch opgehaald en alles wordt voor dashboard en rapportages naar EUR omgerekend.",
  },
];

/** Help-venster (vraagteken in de Topbar): sneltoetsen, veelgestelde vragen en
 *  verwijzingen naar de juiste instellingenpagina. */
export function HelpModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Hulp & snelstart" size="lg">
      <div className="space-y-6">
        {/* Sneltoetsen */}
        <section>
          <h3 className="flex items-center gap-2 text-sm font-bold text-slate-800 mb-3">
            <Keyboard size={16} className="text-slate-400" /> Sneltoetsen
          </h3>
          <div className="grid sm:grid-cols-2 gap-2">
            <div className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5">
              <span className="flex items-center gap-2 text-sm text-slate-600">
                <Search size={14} className="text-slate-400" /> Globaal zoeken
              </span>
              <kbd className="rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] font-semibold text-slate-500">
                Ctrl K
              </kbd>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5">
              <span className="flex items-center gap-2 text-sm text-slate-600">
                <Bell size={14} className="text-slate-400" /> Meldingen openen
              </span>
              <kbd className="rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] font-semibold text-slate-500">
                Bel-icoon
              </kbd>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section>
          <h3 className="text-sm font-bold text-slate-800 mb-2">Veelgestelde vragen</h3>
          <div className="rounded-xl border border-slate-100 divide-y divide-slate-100 overflow-hidden">
            {FAQ_ITEMS.map((item, i) => {
              const open = openIdx === i;
              return (
                <div key={i}>
                  <button
                    onClick={() => setOpenIdx(open ? null : i)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                  >
                    <span className="text-sm font-semibold text-slate-700">{item.q}</span>
                    <ChevronDown
                      size={16}
                      className={`text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                    />
                  </button>
                  {open && (
                    <p className="px-4 pb-3.5 text-sm text-slate-500 leading-relaxed">{item.a}</p>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Verder naar instellingen */}
        <section className="rounded-xl bg-brand-50/70 border border-brand-100 px-4 py-3.5">
          <p className="text-sm text-slate-600">
            Voor e-mailconfiguratie, logo's, BTW-tarieven en teamleden ga je naar{" "}
            <button
              onClick={() => {
                onClose();
                navigate("/settings");
              }}
              className="font-semibold text-brand-700 underline underline-offset-2 hover:text-brand-800"
            >
              Instellingen
            </button>
            . Vragen over je abonnement of facturatie van AIBoek zelf? Neem contact op met AICORE.
          </p>
        </section>
      </div>
    </Modal>
  );
}